// src/types/chat.ts
export interface Attachment {
  filename: string;
  content: string;  // Base64 encoded content
  content_type: string;  // MIME type
  size: number;  // File size in bytes
}

export interface Message {
  id: string;
  type: 'user' | 'system';
  content: string;
  timestamp: Date;
  attachments?: Attachment[];
  intent?: string;  // Backend agent intent (e.g., 'research', 'code', 'dual_code', 'general')
  is_dashboard?: boolean;  // Backend dashboard flag (true for visualizations - Python or HTML)
  is_dual_code?: boolean;  // Backend dual code flag (true for side-by-side language comparison)
}

export interface ChatRequest {
  message: string;
  attachments?: Attachment[];
  // Learning Path attribution (paths step 3): set while the chat is a path
  // step — the backend stores it in learning_events so the node turns green.
  path_id?: string;
  node_id?: string;
  // Acquisition attribution for deep-linked prompts (e.g. source='blog',
  // source_ref='<post slug>' from a blog "Run in Cubemate" button). One-shot:
  // sent with the first message only; backend stores it in learning_events.
  source?: string;
  source_ref?: string;
}

export interface ChatResponse {
  response: string;
  codeBlocks: CodeBlock[];
}

export interface CodeBlock {
  language: string;
  code: string;
  filename?: string;
}

// New authentication types
export interface User {
  id: number;
  username: string;
  email: string;
  created_at: string;
  last_login?: string;
  is_active: boolean;
  subscription_tier?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  user: User;
}

export interface Session {
  session_id: string;
}

export interface CodeCopyEvent {
  message_id: string;
  code_block_index: number;
  was_copied: boolean;
}

export interface UserStats {
  sessions: {
    total_sessions: number;
    avg_messages_per_session: number;
    total_cost: number;
    avg_session_duration: number;
    bounce_sessions: number;
  };
  code_interactions: {
    total_code_blocks: number;
    copied_code_blocks: number;
    languages_used: number;
  };
}

// src/types/chat.ts - Add this interface if it doesn't exist
export interface ExecutionResult {
  success: boolean;
  output?: string;
  error?: string;
  execution_time?: string;
  memory_used?: string;
  status?: string;
  language?: string;
}

// Workflow types for algorithm comparison
export interface WorkflowCodeFile {
  filename: string;
  language: string;
  code: string;
  description?: string;
}

export interface WorkflowGenerateRequest {
  description: string;
  session_id: string;
  user_id: number;
}

export interface WorkflowGenerateResponse {
  execution_id: string;
  status: string;
  generated_files: WorkflowCodeFile[];
  message?: string;
}

export interface LanguageExecutionResult {
  language: string;
  filename: string;
  success: boolean;
  execution_time_ms?: number;
  stdout?: string;
  stderr?: string;
  error_message?: string;
  executor_type?: string;
}

export interface WorkflowComparison {
  fastest_language?: string;
  slowest_language?: string;
  fastest_time_ms?: number;
  slowest_time_ms?: number;
  performance_ratio?: number;
  total_files_executed: number;
  successful_executions: number;
  failed_executions: number;
  insights?: string;
}

export interface WorkflowStatusResponse {
  execution_id: string;
  status: string;
  progress_percent: number;
  description: string;
  generated_files: WorkflowCodeFile[];
  execution_results: LanguageExecutionResult[];
  comparison?: WorkflowComparison;
  error_message?: string;
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

// Codebook types for interactive programming tutorials

// Content schemas per block type
export interface NarrativeContent {
  markdown: string;
}

export interface CodeContent {
  language: string;
  code: string;
  explanation?: string;
  executable?: boolean;
  // SQL blocks only: hidden schema+seed script executed before the visible SQL
  setup_sql?: string;
}

export interface CodeDualContent {
  primary: { language: string; code: string };
  secondary: { language: string; code: string };
  comparison_notes?: string;
}

export interface NextTopicsContent {
  topics: string[];
}

// The unified envelope
export interface CodebookBlock {
  artifact_id: string;
  type: 'narrative' | 'code' | 'code_dual' | 'next_topics';
  version: number;
  metadata: Record<string, any>;
  content: NarrativeContent | CodeContent | CodeDualContent | NextTopicsContent;
}

// Type guards
export function isNarrativeBlock(block: CodebookBlock): block is CodebookBlock & { content: NarrativeContent } {
  return block.type === 'narrative';
}
export function isCodeBlock(block: CodebookBlock): block is CodebookBlock & { content: CodeContent } {
  return block.type === 'code';
}
export function isCodeDualBlock(block: CodebookBlock): block is CodebookBlock & { content: CodeDualContent } {
  return block.type === 'code_dual';
}
export function isNextTopicsBlock(block: CodebookBlock): block is CodebookBlock & { content: NextTopicsContent } {
  return block.type === 'next_topics';
}

export interface CodebookGenerateRequest {
  prompt: string;  // User prompt
  session_id: string;
  // Learning Path attribution (paths step 3) — see ChatRequest.
  path_id?: string;
  node_id?: string;
}

export interface CodebookGenerateResponse {
  codebook_id: string;
  status: string;  // generating, completed, failed
  title: string;
  blocks: CodebookBlock[];
  message?: string;
}

export interface CodebookGetResponse {
  codebook_id: string;
  user_id: number;
  title: string;
  prompt: string;
  blocks: CodebookBlock[];
  status: string;
  total_blocks: number;
  narrative_blocks: number;
  code_blocks: number;
  word_count: number;
  is_public?: boolean;
  created_at: string;
  completed_at?: string;
  last_accessed_at: string;
  error_message?: string;
}

export interface CodebookShareResponse {
  is_public: boolean;
  share_url?: string;
}

export interface CodebookListItem {
  codebook_id: string;
  title: string;
  prompt: string;
  status: string;
  total_blocks: number;
  created_at: string;
  completed_at?: string;
}

export interface CodebookListResponse {
  codebooks: CodebookListItem[];
  total: number;
  offset: number;
  limit: number;
}